import React, {useState, useEffect, useCallback} from 'react';
import {agent} from 'core/api';
import {User} from 'models/user';
import {createHook, createProvider, ProviderProps} from 'utils';

interface TokenData {
    id: number;
}

export type SessionStore = ReturnType<typeof useSessionStore>;

function useSessionStore() {
    const [loading, setLoading] = useState(true);
    const [loggedIn, setLoggedIn] = useState(false);
    const [account, setAccount] = useState<User>();

    const authenticate = useCallback(async () => {
        try {
            const tokenData = await agent.users.authenticate() as TokenData;
            const account = await agent.users.getById(tokenData.id, {
                populate: ['translator', 'roles'],
            });
            setAccount(account);
            setLoggedIn(true);
        } catch (e) {
            setLoggedIn(false);
        }
    }, []);

    const login = useCallback(async (username: string, password: string) => {
        setLoading(true);

        try {
            await agent.users.login({username, password});
            await authenticate();
            setLoading(false);
            return true;
        } catch (e) {
            setLoggedIn(false);
            setLoading(false);
            return false;
        }
    }, [authenticate]);

    const logout = useCallback(async () => {
        try {
            setLoggedIn(false);
            await agent.users.logout();
            localStorage.removeItem('refresh-token');
        } catch (e) {

        }
    }, []);

    const hasRole = useCallback((...tags: string[]) => {
        const roles = account?.roles;
        if (!roles) return false;

        for (const tag of tags) {
            const role = roles.find(el => el.tag === tag);
            if (role) return true;
        }
        return false;
    }, [account]);

    const hasAllLanguagesAllowed = useCallback(() => {
        return account?.translator?.allowAllLanguages;
    }, [account]);

    useEffect(() => {
        authenticate().finally(() => setLoading(false));
    }, [authenticate]);

    return {
        account,
        hasRole,
        loading,
        loggedIn,
        login,
        logout,
        hasAllLanguagesAllowed
    };
}

const Context = React.createContext<SessionStore | null>(null);

export const AccountStoreProvider = (props: ProviderProps) => {
    const sessionStore = useSessionStore();
    return createProvider(Context, sessionStore, props);
};
export const useAccountStore = createHook(Context, 'Account Store', false);